/**
 * Isolated print HTML for vault documents (no React/JSX — safe for Node tests).
 */
function escapeHtml(s) {
  return String(s || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export const PRINT_PAGE_CSS = `
  @page { size: auto; margin: 12mm; }
  html, body {
    margin: 0;
    padding: 0;
    background: #fff;
    color: #111827;
    font-family: system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, sans-serif;
  }
  body { padding: 8mm; }
  .no-print { display: none !important; }
  .vault-print-doc h1, .vault-print-doc .print-title {
    font-size: 22px;
    font-weight: 700;
    margin: 0 0 8px;
    line-height: 1.25;
  }
  .vault-print-doc .print-purpose {
    font-size: 14px;
    color: #4b5563;
    margin: 0 0 16px;
  }
  .vault-md-doc code {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.92em;
    word-break: break-word;
    overflow-wrap: anywhere;
    white-space: pre-wrap;
  }
  .vault-md-table-wrap {
    width: 100%;
    max-width: 100%;
    margin: 12px 0;
    overflow: visible;
  }
  .vault-md-table {
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    font-size: 11px;
  }
  .vault-md-th, .vault-md-td {
    border: 1px solid #e5e7eb;
    padding: 5px 6px;
    text-align: left;
    vertical-align: top;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
  .vault-md-th { font-weight: 700; background: #f9fafb; }
  .vault-md-doc pre {
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: anywhere;
  }
`;

/** Build isolated print HTML (document only — no admin shell). */
export function buildVaultPrintHtml(rootEl, title) {
  if (!rootEl) return "";
  const clone = rootEl.cloneNode(true);
  if (typeof clone.querySelectorAll === "function") {
    clone.querySelectorAll("button, .MuiAlert-root, .no-print, style").forEach((n) => n.remove());
  }
  const safeTitle = escapeHtml(title || "Vault document");
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>${safeTitle}</title>
  <style>${PRINT_PAGE_CSS}</style>
</head>
<body>
  <div class="vault-print-doc">${clone.innerHTML}</div>
</body>
</html>`;
}

/**
 * Print ONLY the document via a same-origin hidden iframe.
 * No visible about:blank tab / pop-up permission dance.
 */
export function printVaultDocumentOnly(rootEl, title) {
  if (!rootEl || typeof window === "undefined" || typeof document === "undefined") return;

  const html = buildVaultPrintHtml(rootEl, title);
  if (!html) return;

  const iframe = document.createElement("iframe");
  iframe.setAttribute("title", "vault-document-print");
  iframe.setAttribute("aria-hidden", "true");
  iframe.style.cssText =
    "position:fixed;right:0;bottom:0;width:0;height:0;border:0;opacity:0;pointer-events:none;";
  document.body.appendChild(iframe);

  const frameDoc = iframe.contentDocument || iframe.contentWindow?.document;
  if (!frameDoc) {
    document.body.removeChild(iframe);
    window.alert(
      "Could not prepare print view. Try again, or use Share link and your browser print on the document view.",
    );
    return;
  }

  frameDoc.open();
  frameDoc.write(html);
  frameDoc.close();

  const cleanup = () => {
    try {
      if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
    } catch {
      /* ignore */
    }
  };

  const runPrint = () => {
    try {
      iframe.contentWindow?.focus();
      iframe.contentWindow?.print();
    } finally {
      setTimeout(cleanup, 1000);
    }
  };

  setTimeout(runPrint, 100);
}
